import { useState, useEffect, useCallback } from 'react';
import type { DashboardOverview, PageListItem, AIContextSummary, GSCDailyMetrics, TrendStatus } from '@ongrid/shared';
import {
  fetchOverview,
  fetchPages,
  fetchPageDetail,
  fetchTopPerformers,
  fetchNeedsAttention,
} from './api';

/**
 * Hook for fetching dashboard overview
 */
export function useOverview() {
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const overview = await fetchOverview();
      setData(overview);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

/**
 * Hook for fetching paginated pages
 */
export function usePages(params: {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  trendStatus?: TrendStatus | null;
  search?: string;
}) {
  const [data, setData] = useState<PageListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchPages(params);
      setData(result.items);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [params.page, params.pageSize, params.sortBy, params.sortOrder, params.trendStatus, params.search]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, total, loading, error, refetch };
}

/**
 * Hook for fetching single page detail
 */
export function usePageDetail(pageUrl: string | null) {
  const [summary, setSummary] = useState<AIContextSummary | null>(null);
  const [history, setHistory] = useState<GSCDailyMetrics[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!pageUrl) return;

    setLoading(true);
    setError(null);
    try {
      const result = await fetchPageDetail(pageUrl);
      setSummary(result.summary);
      setHistory(result.history);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [pageUrl]);

  useEffect(() => {
    if (pageUrl) {
      refetch();
    } else {
      setSummary(null);
      setHistory([]);
    }
  }, [pageUrl, refetch]);

  return { summary, history, loading, error, refetch };
}

/**
 * Hook for fetching top performers
 */
export function useTopPerformers(limit: number = 10) {
  const [data, setData] = useState<PageListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchTopPerformers(limit)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Unknown error'))
      .finally(() => setLoading(false));
  }, [limit]);

  return { data, loading, error };
}

/**
 * Hook for fetching pages needing attention
 */
export function useNeedsAttention(limit: number = 10) {
  const [data, setData] = useState<PageListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchNeedsAttention(limit)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Unknown error'))
      .finally(() => setLoading(false));
  }, [limit]);

  return { data, loading, error };
}

